import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaUser, FaPhone, FaPassport, FaCoins, FaUserTie, FaEye, FaEdit, FaTrash, FaCreditCard, FaMoneyBillWave } from 'react-icons/fa';

const TravelerCard = ({ traveler, campaignName, paymentStatus, onView, onEdit, onDelete }) => {
  const { t } = useTranslation('travelers');

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'unpaid':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getPaymentTypeIcon = (type) => {
    return type === 'cash' ? <FaMoneyBillWave /> : <FaCreditCard />;
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return `0 ${t('common:currency')}`;
    }
    return `${amount.toLocaleString()} ${t('common:currency')}`;
  };

  const getPaymentProgress = () => {
    if (!traveler.totalAmount || traveler.totalAmount === 0 || traveler.remainingBalance === undefined) {
      return 0;
    }
    const paidAmount = traveler.totalAmount - (traveler.remainingBalance || 0);
    const percentage = (paidAmount / traveler.totalAmount) * 100;
    return Math.round(Math.max(0, Math.min(100, percentage)));
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
              <FaUser className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                {traveler.fullName}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {campaignName}
              </p>
            </div>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(paymentStatus)}`}>
            {t(`paymentStatus.${paymentStatus}`)}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Contact Information */}
        <div className="grid grid-cols-1 gap-2">
          <div className="flex items-center gap-2 text-sm">
            <FaPhone className="text-gray-400 w-4" />
            <span className="text-gray-600 dark:text-gray-400">{t('primaryPhone')}:</span>
            <span className="text-gray-900 dark:text-white font-medium">{traveler.primaryPhone}</span>
          </div>
          {traveler.secondaryPhone && (
            <div className="flex items-center gap-2 text-sm">
              <FaPhone className="text-gray-400 w-4" />
              <span className="text-gray-600 dark:text-gray-400">{t('secondaryPhone')}:</span>
              <span className="text-gray-900 dark:text-white font-medium">{traveler.secondaryPhone}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm">
            <FaPassport className="text-gray-400 w-4" />
            <span className="text-gray-600 dark:text-gray-400">{t('passportNumber')}:</span>
            <span className="text-gray-900 dark:text-white font-medium">{traveler.passportNumber}</span>
          </div>
        </div>

        {/* Financial Information */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('totalAmount')}:</span>
            <span className="font-semibold text-gray-900 dark:text-white">
              {formatCurrency(traveler.totalAmount)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('remainingBalance')}:</span>
            <span className={`font-semibold ${
              traveler.remainingBalance === 0 
                ? 'text-green-600 dark:text-green-400' 
                : 'text-red-600 dark:text-red-400'
            }`}>
              {formatCurrency(traveler.remainingBalance)}
            </span>
          </div>
          
          {/* Payment Progress Bar */}
          <div className="mt-2">
            <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
              <span>{t('paymentProgress')}</span>
              <span>{getPaymentProgress()}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${getPaymentProgress()}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Payment Type */}
        <div className="flex items-center gap-2 text-sm">
          {getPaymentTypeIcon(traveler.paymentType)}
          <span className="text-gray-600 dark:text-gray-400">{t('paymentType')}:</span>
          <span className="text-gray-900 dark:text-white font-medium">
            {t(`paymentTypes.${traveler.paymentType}`)}
          </span>
        </div>

        {/* Agent Information */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <FaUserTie className="text-gray-400 w-4" />
            <span className="text-gray-600 dark:text-gray-400">{t('agent')}:</span>
            <span className="text-gray-900 dark:text-white font-medium">{traveler.agentName}</span>
          </div>
          <div className="flex items-center gap-1">
            <FaCoins className="text-green-500 w-3" />
            <span className="text-green-600 dark:text-green-400 font-medium">
              {formatCurrency(traveler.agentCommission)}
            </span>
          </div>
        </div>

        {/* Installment Information (if applicable) */}
        {traveler.paymentType === 'installments' && traveler.paymentPlan && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 space-y-1">
            <div className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-2">
              {t('installmentPlan')}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('downPayment')}:</span>
                <span className="ml-1 font-medium text-gray-900 dark:text-white">
                  {formatCurrency(traveler.paymentPlan.downPayment)}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('monthlyAmount')}:</span>
                <span className="ml-1 font-medium text-gray-900 dark:text-white">
                  {formatCurrency(traveler.paymentPlan.monthlyAmount)}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('duration')}:</span>
                <span className="ml-1 font-medium text-gray-900 dark:text-white">
                  {traveler.paymentPlan.durationMonths} {t('months')}
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">{t('nextPayment')}:</span>
                <span className="ml-1 font-medium text-gray-900 dark:text-white">
                  {new Date(traveler.paymentPlan.nextPaymentDate).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex justify-end gap-2">
          <button
            onClick={onView}
            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            title={t('viewDetails')}
          >
            <FaEye />
          </button>
          <button
            onClick={onEdit}
            className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
            title={t('edit')}
          >
            <FaEdit />
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            title={t('delete')}
          >
            <FaTrash />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TravelerCard;